import { ChangeEvent, ReactNode, useCallback, useMemo } from "react";

export type DataGridColumn<Row> = {
	/** Stable column key. Also the property read off the row when `render` is omitted. */
	key: string;
	header: ReactNode;
	/** Cell content. Without it the grid renders `row[key]`, or "—" when that is empty. */
	render?: (row: Row, rowIndex: number) => ReactNode;
	/** Any CSS length. Omit to let the column size itself. */
	width?: string;
	align?: "left" | "right" | "center";
	/** Shows a search box for this column in the filter row. */
	filterable?: boolean;
	filterPlaceholder?: string;
	/**
	 * Stops `onRowClick` firing from inside this cell. Set it on columns holding
	 * their own controls, so a button press does not also open the row.
	 */
	stopRowClick?: boolean;
	/** Extra classes for this column's header and body cells. */
	className?: string;
};

export type DataGridPagination = {
	currentPage: number;
	pageCount?: number;
	onPageChange: (page: number) => void;
};

export type DataGridProps<Row> = {
	rows: Row[];
	columns: DataGridColumn<Row>[];
	/** Row identity, used for keys and for selection. */
	getRowId: (row: Row, index: number) => string;
	loading?: boolean;
	emptyMessage?: ReactNode;
	onRowClick?: (row: Row) => void;
	/** Extra classes on a row, e.g. `opacity-60` for completed records. */
	getRowClassName?: (row: Row) => string | undefined;
	/** Current filter values keyed by column key. `onFilterChange` turns the filter row on. */
	filters?: Record<string, string>;
	onFilterChange?: (key: string, value: string) => void;
	/** Checkbox selection. Supplying both turns the checkbox column on. */
	selectedIds?: string[];
	onSelectionChange?: (ids: string[]) => void;
	pagination?: DataGridPagination;
	/** Minimum table width before the wrapper scrolls horizontally. */
	minWidth?: string;
};

const PAGE_WINDOW = 2;

const ALIGN_CLASS = {
	left: "text-left",
	right: "text-right",
	center: "text-center"
} as const;

/** Page numbers to render, with `null` standing in for a gap. */
const buildPageList = (currentPage: number, pageCount: number): (number | null)[] => {
	const pages: (number | null)[] = [];
	for (let page = 1; page <= pageCount; page++) {
		const isEdge = page === 1 || page === pageCount;
		const isNearCurrent = Math.abs(page - currentPage) <= PAGE_WINDOW;
		if (isEdge || isNearCurrent) {
			pages.push(page);
		} else if (pages[pages.length - 1] !== null) {
			pages.push(null);
		}
	}
	return pages;
};

const readCell = <Row,>(row: Row, key: string): ReactNode => {
	const value = (row as Record<string, unknown>)[key];
	if (value === null || value === undefined || value === "") {
		return "—";
	}
	if (typeof value === "boolean") {
		return value ? "Yes" : "No";
	}
	if (typeof value === "object") {
		return "—";
	}
	return String(value);
};

const PAGE_BUTTON =
	"min-w-[2.25rem] rounded-md border border-line-strong bg-surface px-2.5 py-1.5 font-[inherit] " +
	"text-[13px] font-semibold text-body cursor-pointer disabled:cursor-default disabled:text-faint";

/**
 * The CRM's one table. Rendering, filtering and paging are all driven from
 * props: the grid holds no data state of its own, so screens keep owning their
 * server-side queries exactly as they did with ag-Grid.
 */
function DataGrid<Row>({
	rows,
	columns,
	getRowId,
	loading = false,
	emptyMessage = "Nothing to show yet.",
	onRowClick,
	getRowClassName,
	filters,
	onFilterChange,
	selectedIds,
	onSelectionChange,
	pagination,
	minWidth = "56rem"
}: DataGridProps<Row>): JSX.Element {
	const selectable = Boolean(selectedIds && onSelectionChange);
	const showFilters = Boolean(onFilterChange && columns.some((column) => column.filterable));

	const rowIds = useMemo(() => rows.map((row, index) => getRowId(row, index)), [rows, getRowId]);
	const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds?.includes(id));

	const handleToggleAll = useCallback(() => {
		onSelectionChange?.(allSelected ? [] : rowIds);
	}, [allSelected, onSelectionChange, rowIds]);

	const handleToggleRow = useCallback(
		(id: string) => {
			if (!onSelectionChange || !selectedIds) {
				return;
			}
			onSelectionChange(
				selectedIds.includes(id) ? selectedIds.filter((current) => current !== id) : [...selectedIds, id]
			);
		},
		[onSelectionChange, selectedIds]
	);

	const columnCount = columns.length + (selectable ? 1 : 0);
	const pageCount = pagination?.pageCount ?? 1;

	return (
		<div className="ui-grid text-body">
			<div className="overflow-x-auto rounded-lg border border-line bg-surface">
				<table className="w-full border-collapse text-sm" style={{ minWidth }}>
					<thead>
						<tr>
							{selectable && (
								<th className="w-12 bg-header px-4 py-3 text-center">
									<input
										type="checkbox"
										className="h-4 w-4 cursor-pointer accent-brand-green"
										checked={allSelected}
										onChange={handleToggleAll}
										aria-label="Select all rows"
									/>
								</th>
							)}
							{columns.map((column) => (
								<th
									key={column.key}
									style={{ width: column.width }}
									className={[
										"whitespace-nowrap bg-header px-4 py-3 text-[13px] font-bold text-header-fg",
										ALIGN_CLASS[column.align ?? "left"],
										column.className ?? ""
									].join(" ")}
								>
									{column.header}
								</th>
							))}
						</tr>

						{showFilters && (
							<tr>
								{selectable && <td className="w-12 border-b border-line bg-[#eef1ea]" />}
								{columns.map((column) => (
									<td key={column.key} className="border-b border-line bg-[#eef1ea] px-2.5 py-2">
										{column.filterable && (
											<input
												type="search"
												value={filters?.[column.key] ?? ""}
												placeholder={column.filterPlaceholder ?? "Search"}
												aria-label={`Filter by ${column.key}`}
												onChange={(event: ChangeEvent<HTMLInputElement>) =>
													onFilterChange?.(column.key, event.target.value)
												}
												className="w-full min-w-[6rem] rounded-md border border-line-strong bg-surface px-2.5 py-1.5 font-[inherit] text-[13px] text-body outline-none focus:border-brand-green focus:shadow-focus"
											/>
										)}
									</td>
								))}
							</tr>
						)}
					</thead>

					<tbody>
						{loading && (
							<tr>
								<td colSpan={columnCount} className="px-4 py-10 text-center text-muted">
									Loading…
								</td>
							</tr>
						)}

						{!loading && rows.length === 0 && (
							<tr>
								<td colSpan={columnCount} className="px-4 py-10 text-center text-muted">
									{emptyMessage}
								</td>
							</tr>
						)}

						{!loading &&
							rows.map((row, rowIndex) => {
								const id = rowIds[rowIndex];
								const cellBorder = rowIndex === 0 ? "" : "border-t border-line";
								return (
									<tr
										key={id}
										onClick={onRowClick ? () => onRowClick(row) : undefined}
										className={[
											onRowClick ? "cursor-pointer hover:bg-row-hover" : "",
											getRowClassName?.(row) ?? ""
										].join(" ")}
									>
										{selectable && (
											<td
												className={`w-12 px-4 py-3 text-center ${cellBorder}`}
												onClick={(event) => event.stopPropagation()}
											>
												<input
													type="checkbox"
													className="h-4 w-4 cursor-pointer accent-brand-green"
													checked={selectedIds?.includes(id) ?? false}
													onChange={() => handleToggleRow(id)}
													aria-label="Select row"
												/>
											</td>
										)}
										{columns.map((column) => (
											<td
												key={column.key}
												onClick={
													column.stopRowClick ? (event) => event.stopPropagation() : undefined
												}
												className={[
													"px-4 py-3 align-middle",
													cellBorder,
													ALIGN_CLASS[column.align ?? "left"],
													column.className ?? ""
												].join(" ")}
											>
												{column.render
													? column.render(row, rowIndex)
													: readCell(row, column.key)}
											</td>
										))}
									</tr>
								);
							})}
					</tbody>
				</table>
			</div>

			{pagination && pageCount > 1 && (
				<nav className="flex flex-wrap items-center justify-end gap-1.5 px-1 pt-3.5" aria-label="Pagination">
					<button
						type="button"
						className={`${PAGE_BUTTON} hover:bg-row-hover`}
						disabled={pagination.currentPage <= 1}
						onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
					>
						Previous
					</button>

					{buildPageList(pagination.currentPage, pageCount).map((page, index) =>
						page === null ? (
							// eslint-disable-next-line react/no-array-index-key
							<span key={`gap-${index}`} className="px-0.5 text-faint">
								…
							</span>
						) : page === pagination.currentPage ? (
							<button
								key={page}
								type="button"
								aria-current="page"
								className={`${PAGE_BUTTON} cursor-default border-header bg-header text-header-fg`}
							>
								{page}
							</button>
						) : (
							<button
								key={page}
								type="button"
								className={`${PAGE_BUTTON} hover:bg-row-hover`}
								onClick={() => pagination.onPageChange(page)}
							>
								{page}
							</button>
						)
					)}

					<button
						type="button"
						className={`${PAGE_BUTTON} hover:bg-row-hover`}
						disabled={pagination.currentPage >= pageCount}
						onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
					>
						Next
					</button>
				</nav>
			)}
		</div>
	);
}

export default DataGrid;
